import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useParams } from 'react-router-dom'
import { fetchData, fetchJson } from '../../api/fetchJson'
import { orgPath } from '../../config/orgId'
import { useVendors } from '../vendors/VendorListPage'
import { useBankAccounts } from '../bank-accounts/useBankAccounts'
import {
  Button,
  Card,
  Currency,
  Empty,
  ErrorBanner,
  Field,
  FormattedDate,
  Input,
  Loading,
  PageHeader,
  Select,
  Tag,
} from '../../components/ui'
import { flagIfAuthError } from '../../components/ResetBanner'

type Bill = {
  id: string
  bill_number?: string
  bill_date?: number
  total_amount?: number
  paid_amount?: number
  balance?: number
  status?: string
  notes?: string | null
}

type Payment = {
  id: string
  payment_date?: number
  amount?: number
  payment_method?: string | null
  reference_number?: string | null
}

export function VendorDetailPage() {
  const { vendorId } = useParams<{ vendorId: string }>()
  const vendors = useVendors()
  const vendor = vendors.data?.find((v) => v.id === vendorId)
  const bankAccounts = useBankAccounts()
  const activeBankAccounts = (bankAccounts.data ?? []).filter((a) => a.is_active)
  const qc = useQueryClient()

  const bills = useQuery<Bill[]>({
    queryKey: ['vendor-bills', vendorId],
    enabled: Boolean(vendorId),
    queryFn: () => fetchData<Bill[]>(orgPath(`/vendors/${vendorId}/bills`)),
  })

  const payments = useQuery<Payment[]>({
    queryKey: ['vendor-payments', vendorId],
    enabled: Boolean(vendorId),
    queryFn: () => fetchData<Payment[]>(orgPath(`/vendors/${vendorId}/payments`)),
  })

  const [amount, setAmount] = useState('')
  const [mode, setMode] = useState<'cash' | 'bank_transfer'>('cash')
  const [bankAccountId, setBankAccountId] = useState('')
  const [reference, setReference] = useState('')

  const recordPayment = useMutation({
    mutationFn: () => {
      return fetchJson(orgPath(`/vendors/${vendorId}/payments`), {
        method: 'POST',
        body: JSON.stringify({
          amount: Number(amount),
          payment_method: mode,
          bank_account_id: mode === 'bank_transfer' ? bankAccountId : undefined,
          reference_number: reference || undefined,
          payment_date: Math.floor(Date.now() / 1000),
        }),
      })
    },
    onError: flagIfAuthError,
    onSuccess: () => {
      setAmount('')
      setReference('')
      qc.invalidateQueries({ queryKey: ['vendor-payments', vendorId] })
      qc.invalidateQueries({ queryKey: ['vendor-bills', vendorId] })
    },
  })

  if (vendors.isLoading || bills.isLoading || payments.isLoading) return <Loading />
  if (bills.isError) return <ErrorBanner error={bills.error} />

  return (
    <div className="space-y-6">
      <PageHeader
        title={vendor?.name ?? 'Vendor'}
        subtitle={vendor?.gst_number ? `GST: ${vendor.gst_number}` : undefined}
      />

      <section>
        <h2 className="font-semibold mb-3">Bills</h2>
        {!bills.data || bills.data.length === 0 ? (
          <Empty message="No bills filed for this vendor yet." />
        ) : (
          <Card>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-surface-400 border-b border-surface-700">
                    <th className="py-2 pr-4">Bill</th>
                    <th className="py-2 pr-4">Date</th>
                    <th className="py-2 pr-4">Total</th>
                    <th className="py-2 pr-4">Paid</th>
                    <th className="py-2 pr-4">Balance</th>
                    <th className="py-2">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {bills.data.map((b) => (
                    <tr key={b.id} className="border-b border-surface-700/50">
                      <td className="py-2 pr-4">{b.bill_number ?? b.id.slice(0, 8)}</td>
                      <td className="py-2 pr-4">
                        <FormattedDate ts={b.bill_date ?? null} />
                      </td>
                      <td className="py-2 pr-4">
                        <Currency amount={b.total_amount ?? null} />
                      </td>
                      <td className="py-2 pr-4">
                        <Currency amount={b.paid_amount ?? null} />
                      </td>
                      <td className="py-2 pr-4">
                        <Currency amount={b.balance ?? null} />
                      </td>
                      <td className="py-2">
                        <Tag tone={b.status === 'paid' ? 'success' : 'warn'}>
                          {b.status ?? '—'}
                        </Tag>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      <section className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h2 className="font-semibold mb-3">Record vendor payment</h2>
          <form
            className="space-y-3"
            onSubmit={(e) => {
              e.preventDefault()
              recordPayment.mutate()
            }}
          >
            <Field label="Amount">
              <Input
                type="number"
                min={0}
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                required
              />
            </Field>
            <Field label="Mode">
              <Select
                value={mode}
                onChange={(e) => {
                  const next = e.target.value as 'cash' | 'bank_transfer'
                  setMode(next)
                  if (next === 'bank_transfer' && !bankAccountId && activeBankAccounts.length === 1) {
                    setBankAccountId(activeBankAccounts[0].id)
                  }
                }}
              >
                <option value="cash">Cash</option>
                <option value="bank_transfer">Bank transfer</option>
              </Select>
            </Field>
            {mode === 'bank_transfer' && (
              <Field label="Bank account">
                <Select
                  value={bankAccountId}
                  onChange={(e) => setBankAccountId(e.target.value)}
                  required
                >
                  <option value="">Select…</option>
                  {activeBankAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.account_name} — {a.bank_name}
                    </option>
                  ))}
                </Select>
                {activeBankAccounts.length === 0 && !bankAccounts.isLoading && (
                  <p className="text-xs text-amber-300 mt-1">
                    No bank accounts seeded. Operator: re-run the demo seed.
                  </p>
                )}
              </Field>
            )}
            <Field label="Reference (optional)">
              <Input
                type="text"
                value={reference}
                onChange={(e) => setReference(e.target.value)}
              />
            </Field>
            {recordPayment.isError && <ErrorBanner error={recordPayment.error} />}
            <Button
              type="submit"
              disabled={
                recordPayment.isPending ||
                (mode === 'bank_transfer' && !bankAccountId)
              }
            >
              {recordPayment.isPending ? 'Recording…' : 'Record payment'}
            </Button>
            <p className="text-xs text-surface-400">
              Settles open bills FIFO on the backend.
            </p>
          </form>
        </Card>

        <Card>
          <h2 className="font-semibold mb-3">Recent payments</h2>
          {!payments.data || payments.data.length === 0 ? (
            <p className="text-sm text-surface-400">None yet.</p>
          ) : (
            <ul className="divide-y divide-surface-700 text-sm">
              {payments.data.slice(0, 8).map((p) => (
                <li key={p.id} className="py-2 flex justify-between gap-3">
                  <div>
                    <Currency amount={p.amount ?? null} />
                    {p.payment_method && (
                      <span className="text-xs text-surface-400 ml-2">{p.payment_method}</span>
                    )}
                  </div>
                  <div className="text-xs text-surface-400">
                    <FormattedDate ts={p.payment_date ?? null} />
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    </div>
  )
}
